import type { GoalFilters } from '@/features/goals/types/goal.types'
import type { TaskFilters } from '@/features/tasks/types/task.types'

/**
 * TanStack Query key factory — centralized to prevent cache key collisions.
 *
 * Convention:
 * - Keys are arrays (not strings) for precise cache invalidation
 * - More specific keys are "descendants" of broader keys
 * - Example: invalidating queryKeys.goals.all() also invalidates queryKeys.goals.detail(id)
 *
 * Usage:
 *   const { data } = useQuery({ queryKey: queryKeys.modules.all(), queryFn: ... })
 *   queryClient.invalidateQueries({ queryKey: queryKeys.goals.all() })
 */
export const queryKeys = {
  // Platform
  modules: {
    all: () => ['modules'] as const,
  },

  // Auth
  auth: {
    currentUser: () => ['auth', 'currentUser'] as const,
  },

  // Goals (Phase 2)
  goals: {
    all:    ()                        => ['goals'] as const,
    list:   (filters: GoalFilters)    => ['goals', 'list', filters] as const,
    detail: (id: string)              => ['goals', id] as const,
  },

  // Tasks (Phase 2)
  tasks: {
    all:    ()                        => ['tasks'] as const,
    list:   (filters: TaskFilters)    => ['tasks', 'list', filters] as const,
    detail: (id: string)              => ['tasks', id] as const,
    byGoal: (goalId: string)          => ['tasks', 'byGoal', goalId] as const,
  },

  // Dashboard (Phase 2)
  dashboard: {
    summary: () => ['dashboard', 'summary'] as const,
  },

  // Planner (Phase 3)
  planner: {
    all: () => ['planner'] as const,
    date: (date: string) => ['planner', 'date', date] as const,
  },

  // Calendar (Phase 3)
  calendar: {
    all: () => ['calendar'] as const,
    range: (start: string, end: string) => ['calendar', 'range', start, end] as const,
  },

  // Streak (Phase 3)
  streak: {
    user: () => ['streak'] as const,
  },

  // French NCLC 7 Journey Companion
  french: {
    dashboard: () => ['french', 'dashboard'] as const,
    skills: () => ['french', 'skills'] as const,
    streak: () => ['french', 'streak'] as const,
    challenges: (date?: string) => ['french', 'challenges', date] as const,
    challengesByStage: (stage?: string) => ['french', 'challenges', 'stage', stage] as const,
    curriculumTiers: () => ['french', 'curriculum', 'tiers'] as const,
    scoringScales: () => ['french', 'curriculum', 'scales'] as const,
    history: (page?: number) => ['french', 'history', page] as const,
  },

  // Language Goals (CELPIP & French Stage 1)
  languageGoals: {
    all: () => ['language-goals'] as const,
    active: (objectiveType?: string) => ['language-goals', 'active', objectiveType] as const,
    history: () => ['language-goals', 'history'] as const,
    detail: (id: string) => ['language-goals', id] as const,
  },

  // Diagnostic Assessments (Stage 2)
  assessments: {
    all: () => ['language-assessments'] as const,
    list: (goalId?: string) => ['language-assessments', 'list', goalId] as const,
    detail: (id: string) => ['language-assessments', id] as const,
    tasks: (id: string) => ['language-assessments', id, 'tasks'] as const,
    results: (id: string) => ['language-assessments', id, 'results'] as const,
  },

  // Personalized Learning Plans (Stage 3)
  learningPlans: {
    all: () => ['language-plans'] as const,
    current: () => ['language-plans', 'current'] as const,
    progress: () => ['language-plans', 'progress'] as const,
    catalog: () => ['language-plans', 'catalog'] as const,
    catalogItem: (id: string) => ['language-plans', 'catalog', id] as const,
  },

  // Practice & Feedback (Stage 4)
  practice: {
    all: () => ['practice'] as const,
    attempt: (id: string) => ['practice', 'attempt', id] as const,
    history: (goalId: string, plannedActivityId?: string) => ['practice', 'history', goalId, plannedActivityId] as const,
    mistakes: (goalId: string) => ['practice', 'mistakes', goalId] as const,
  },

  // Progress Intelligence & Adaptive Learning (Stage 5)
  progressIntelligence: {
    all: () => ['progress-intelligence'] as const,
    summary: () => ['progress-intelligence', 'summary'] as const,
    skillHistory: (skill: string) => ['progress-intelligence', 'skills', skill] as const,
  },
} as const



